import React, { useState, useCallback } from 'react';
import { api } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { 
  Upload, 
  FileSpreadsheet, 
  CheckCircle, 
  AlertCircle, 
  ArrowRight, 
  ArrowLeft,
  Loader2,
  MapPin,
  Calendar,
  Users,
  DollarSign,
  User,
  Phone,
  Hash,
  RefreshCw,
  Percent
} from 'lucide-react';

const STEPS = [
  { id: 1, name: 'Fichier', icon: Upload },
  { id: 2, name: 'Mapping', icon: MapPin },
  { id: 3, name: 'Import', icon: CheckCircle }
];

const FIELD_ICONS = {
  guest_name: User,
  check_in: Calendar,
  check_out: Calendar,
  total_amount: DollarSign,
  commission_amount: Percent,
  currency: DollarSign,
  adults: Users,
  children: Users,
  confirmation_code: Hash,
  phone: Phone,
  email: User,
  status: CheckCircle
};

const FIELD_LABELS = {
  guest_name: 'Nom du client',
  check_in: 'Date d\'arrivée',
  check_out: 'Date de départ',
  total_amount: 'Montant total',
  commission_amount: 'Commission (montant)',
  currency: 'Devise',
  adults: 'Adultes',
  children: 'Enfants',
  confirmation_code: 'Code de confirmation',
  phone: 'Téléphone',
  email: 'Email',
  status: 'Statut'
};

const REQUIRED_FIELDS = ['guest_name', 'check_in', 'check_out'];

export default function ImportWizard({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(1);
  const [platform, setPlatform] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importResult, setImportResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [defaultCommission, setDefaultCommission] = useState(15); // Taux par défaut (%) pour calculer le montant si non mappé
  const [defaultCurrency, setDefaultCurrency] = useState('EUR'); // Devise par défaut

  const resetWizard = () => {
    setStep(1);
    setPlatform('');
    setFile(null);
    setPreviewData(null);
    setMapping({});
    setImportResult(null);
    setDefaultCommission(15);
    setDefaultCurrency('EUR');
  };

  const handleClose = () => {
    resetWizard();
    onOpenChange(false);
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUploadAndPreview = async () => {
    if (!file || !platform) {
      toast.error('Veuillez sélectionner une plateforme et un fichier');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`/reservations/import/${platform}/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPreviewData(response.data);
      setMapping(response.data.suggestedMapping);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la lecture du fichier');
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteImport = async () => {
    // Validate required fields
    const missingFields = REQUIRED_FIELDS.filter(f => !mapping[f]);
    if (missingFields.length > 0) {
      toast.error(`Champs requis manquants: ${missingFields.map(f => FIELD_LABELS[f]).join(', ')}`);
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/reservations/import/execute', {
        fileId: previewData.fileId,
        platform,
        mapping,
        defaultCommission: defaultCommission, // Taux de commission par défaut si non mappé
        defaultCurrency: defaultCurrency // Devise par défaut si non mappée dans le fichier
      });
      
      setImportResult(response.data);
      setStep(3);
      
      if (response.data.imported.length > 0) {
        toast.success(`${response.data.imported.length} réservation(s) importée(s)`);
        onSuccess?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'import');
    } finally {
      setLoading(false);
    }
  };

  const updateMapping = (field, value) => {
    setMapping(prev => ({ ...prev, [field]: value === 'none' ? null : value }));
  };

  const isFieldMapped = (field) => !!mapping[field];
  const isMappingValid = REQUIRED_FIELDS.every(f => mapping[f]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="import-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Importer des réservations
          </DialogTitle>
        </DialogHeader>

        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-2 py-4 border-b">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            const isActive = step === s.id;
            const isCompleted = step > s.id;
            return (
              <React.Fragment key={s.id}>
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${
                  isActive ? 'bg-primary text-white' : isCompleted ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{s.name}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <ArrowRight className={`w-4 h-4 ${isCompleted ? 'text-green-500' : 'text-gray-300'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto py-4">
          {/* Step 1: File Selection */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <Label className="text-base">Plateforme source</Label>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  {[
                    { id: 'airbnb', name: 'Airbnb', color: 'bg-red-50 border-red-200 hover:bg-red-100', activeColor: 'bg-red-500 text-white' },
                    { id: 'booking', name: 'Booking.com', color: 'bg-blue-50 border-blue-200 hover:bg-blue-100', activeColor: 'bg-blue-500 text-white' },
                    { id: 'direct', name: 'Direct/Autre', color: 'bg-gray-50 border-gray-200 hover:bg-gray-100', activeColor: 'bg-gray-700 text-white' }
                  ].map(p => (
                    <button
                      key={p.id}
                      onClick={() => setPlatform(p.id)}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        platform === p.id ? p.activeColor : p.color
                      }`}
                      data-testid={`platform-${p.id}`}
                    >
                      <p className="font-semibold">{p.name}</p>
                      <p className={`text-xs mt-1 ${platform === p.id ? 'opacity-80' : 'text-muted-foreground'}`}>
                        {p.id === 'airbnb' ? 'CSV' : p.id === 'booking' ? 'XLS/XLSX' : 'CSV/XLS'}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-base">Fichier à importer</Label>
                <div
                  className={`mt-2 border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {file ? (
                    <div className="flex flex-col items-center gap-2">
                      <FileSpreadsheet className="w-12 h-12 text-green-500" />
                      <p className="font-medium">{file.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                      <Button variant="outline" size="sm" onClick={() => setFile(null)}>
                        Changer de fichier
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-12 h-12 text-gray-400" />
                      <p className="font-medium">Glissez-déposez votre fichier ici</p>
                      <p className="text-sm text-muted-foreground">ou</p>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".csv,.xls,.xlsx"
                          onChange={handleFileChange}
                          className="hidden"
                          data-testid="file-input"
                        />
                        <span className="text-primary hover:underline">Parcourir</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Column Mapping */}
          {step === 2 && previewData && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">Correspondance des colonnes</h3>
                  <p className="text-sm text-muted-foreground">
                    {previewData.totalRows} lignes détectées dans le fichier
                  </p>
                </div>
                <Badge variant={isMappingValid ? 'default' : 'secondary'}>
                  {Object.values(mapping).filter(Boolean).length} / {Object.keys(FIELD_LABELS).length} mappés
                </Badge>
              </div>

              <div className="grid gap-3">
                {Object.entries(FIELD_LABELS).map(([field, label]) => {
                  const Icon = FIELD_ICONS[field];
                  const isRequired = REQUIRED_FIELDS.includes(field);
                  return (
                    <div key={field} className={`flex items-center gap-3 p-3 rounded-lg border ${
                      isFieldMapped(field) ? 'border-green-200 bg-green-50/50' : isRequired ? 'border-orange-200 bg-orange-50/50' : 'border-gray-200'
                    }`}>
                      <Icon className={`w-5 h-5 ${isFieldMapped(field) ? 'text-green-600' : 'text-gray-400'}`} />
                      <div className="flex-1">
                        <p className="font-medium text-sm">
                          {label}
                          {isRequired && <span className="text-red-500 ml-1">*</span>}
                        </p>
                      </div>
                      <Select 
                        value={mapping[field] || 'none'} 
                        onValueChange={(v) => updateMapping(field, v)}
                      >
                        <SelectTrigger className="w-[200px]" data-testid={`mapping-${field}`}>
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- Non mappé --</SelectItem>
                          {previewData.columns.map(col => (
                            <SelectItem key={col} value={col}>{col}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {isFieldMapped(field) && <CheckCircle className="w-5 h-5 text-green-500" />}
                    </div>
                  );
                })}
              </div>

              {/* Default commission if not mapped */}
              {!mapping.commission_amount && platform !== 'direct' && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Percent className="w-5 h-5 text-blue-600" />
                    <div className="flex-1">
                      <p className="font-medium text-sm text-blue-900">Taux de commission par défaut</p>
                      <p className="text-xs text-blue-700">Appliqué si aucune colonne de commission n'est mappée (calcule le montant)</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={defaultCommission}
                        onChange={(e) => setDefaultCommission(parseFloat(e.target.value) || 0)}
                        className="w-20 text-center"
                        data-testid="default-commission"
                      />
                      <span className="text-sm font-medium text-blue-700">%</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Default currency selector */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-amber-600" />
                  <div className="flex-1">
                    <p className="font-medium text-sm text-amber-900">Devise des montants</p>
                    <p className="text-xs text-amber-700">
                      {mapping.currency 
                        ? 'Détectée depuis la colonne mappée' 
                        : 'Sélectionnez la devise utilisée dans le fichier'}
                    </p>
                  </div>
                  <Select 
                    value={mapping.currency ? 'auto' : defaultCurrency} 
                    onValueChange={(v) => {
                      if (v !== 'auto') setDefaultCurrency(v);
                    }}
                    disabled={!!mapping.currency}
                  >
                    <SelectTrigger className="w-[120px]" data-testid="default-currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {mapping.currency && <SelectItem value="auto">Automatique</SelectItem>}
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                      <SelectItem value="MAD">MAD (DH)</SelectItem>
                      <SelectItem value="USD">USD ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {defaultCurrency === 'EUR' && !mapping.currency && (
                  <p className="text-xs text-amber-700 mt-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Les montants en EUR seront automatiquement convertis en MAD (taux: ~10.8)
                  </p>
                )}
              </div>

              {/* Preview table */}
              <div>
                <h4 className="font-semibold mb-2">Aperçu des données (5 premières lignes)</h4>
                <div className="border rounded-lg overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {previewData.columns.slice(0, 6).map(col => (
                          <th key={col} className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap">
                            {col}
                          </th>
                        ))}
                        {previewData.columns.length > 6 && (
                          <th className="px-3 py-2 text-left font-medium text-gray-500">
                            +{previewData.columns.length - 6} colonnes
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {previewData.preview.slice(0, 5).map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          {previewData.columns.slice(0, 6).map(col => (
                            <td key={col} className="px-3 py-2 whitespace-nowrap max-w-[150px] truncate">
                              {row[col] || '-'}
                            </td>
                          ))}
                          {previewData.columns.length > 6 && (
                            <td className="px-3 py-2 text-gray-400">...</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Results */}
          {step === 3 && importResult && (
            <div className="space-y-6">
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-700">{importResult.summary.imported}</p>
                  <p className="text-sm text-green-600">Importées</p>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                  <RefreshCw className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-orange-700">{importResult.summary.skipped}</p>
                  <p className="text-sm text-orange-600">Ignorées (doublons)</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-red-700">{importResult.summary.errors}</p>
                  <p className="text-sm text-red-600">Erreurs</p>
                </div>
              </div>

              {/* Imported list */}
              {importResult.imported.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 text-green-700">Réservations importées</h4>
                  <div className="border rounded-lg overflow-hidden max-h-[200px] overflow-y-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-green-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Client</th>
                          <th className="px-3 py-2 text-left">Arrivée</th>
                          <th className="px-3 py-2 text-left">Départ</th>
                          <th className="px-3 py-2 text-right">Montant</th>
                          <th className="px-3 py-2 text-right">Commission</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {importResult.imported.map((r, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2">{r.guest}</td>
                            <td className="px-3 py-2">{r.checkIn}</td>
                            <td className="px-3 py-2">{r.checkOut}</td>
                            <td className="px-3 py-2 text-right font-semibold">{r.amount} MAD</td>
                            <td className="px-3 py-2 text-right text-muted-foreground">
                              {r.commissionRate}% ({r.commissionAmount?.toFixed(0)} MAD)
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Errors list */}
              {importResult.errors.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 text-red-700">Erreurs</h4>
                  <div className="border border-red-200 rounded-lg overflow-hidden max-h-[150px] overflow-y-auto">
                    {importResult.errors.map((e, idx) => (
                      <div key={idx} className="px-3 py-2 border-b border-red-100 last:border-0 text-sm">
                        <span className="text-red-600">Ligne {e.row}:</span> {e.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Skipped list */}
              {importResult.skipped.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 text-orange-700">Ignorées (doublons)</h4>
                  <div className="border border-orange-200 rounded-lg overflow-hidden max-h-[150px] overflow-y-auto">
                    {importResult.skipped.map((s, idx) => (
                      <div key={idx} className="px-3 py-2 border-b border-orange-100 last:border-0 text-sm">
                        {s.guest} ({s.ref}) - {s.reason}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          {step === 1 && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Annuler
              </Button>
              <Button 
                onClick={handleUploadAndPreview} 
                disabled={!file || !platform || loading}
                data-testid="next-step-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Suivant
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}
          
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <Button 
                onClick={handleExecuteImport} 
                disabled={!isMappingValid || loading}
                data-testid="import-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Importer {previewData?.totalRows} lignes
              </Button>
            </>
          )}
          
          {step === 3 && (
            <Button onClick={handleClose} data-testid="finish-btn">
              Terminer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
